/**
 * The send path — everything between the user hitting Enter and the
 * USER_MESSAGE ack landing.
 *
 * Three forks share one message identity and one trace id, minted once in
 * `sendMessage` and threaded through all of them:
 *   - `routeTeammateSend` (handlers/teammateSend) when the text opens with a
 *     live teammate's `@name`,
 *   - `sendNewTask` when nothing is selected — mints the task, seeds the
 *     optimistic loader, rolls back or reconciles on the ack,
 *   - `sendContinuation` on an open task — thread entry when idle, a staged
 *     tray card when the agent is busy.
 *
 * Lives outside taskStore.ts for the reason teammateSend and browserFold do:
 * the store calls in, so importing the store back would close a module cycle.
 * The store reach is therefore passed in as `{ get, set }` — which is also the
 * honest list of what the send path touches.
 */

import { EVENTS } from "@/constants/events";
import { generateTaskTitle } from "@/lib/taskTitle";
import { setCurrentTraceId } from "@/lib/clientTrace";
import { isDesktopShell } from "@/lib/desktop-shell";
import { mintTraceId } from "@bash/shared";
import type { FileAttachment } from "@bash/shared";
import { useModeStore } from "../modeStore";
import { useComposerDraftStore } from "../composerDraftStore";
import { useComposerHistoryStore } from "../composerHistoryStore";
import { useRuntimeStore } from "../runtimeStore";
import { useModelStore, resolveComposerModel } from "../modelStore";
import { nextTaskEngine } from "../enginePreferenceStore";
import { buildNewTask } from "../taskFactory";
import { schedulePersist, setStoredTaskId } from "../persistence";
import { getSocket, ensureConnected } from "../socketService";
import { armPerceivedTtft } from "../helpers/perceivedTtft";
import { captureDelegationSubmitted } from "../helpers/delegationSubmitted";
import {
  findMessageByAnyId,
  markMessagePersisted,
} from "../helpers/findMessage";
import { foldSuspendedBrowserOnSend } from "./browserFold";
import { routeTeammateSend } from "./teammateSend";
import {
  emitUserMessage,
  notRecordedCode,
  type NotRecordedCode,
  type SendAck,
} from "./emitUserMessage";
import {
  DEFAULT_AGENT,
  type ContentBlock,
  type ImageAttachment,
  type Message,
} from "../types";
import type { TaskStoreSeam } from "../taskStoreTypes";

/**
 * Phase 3 intelligent ingest — gated behind NEXT_PUBLIC_ENABLE_INTELLIGENT_INGEST.
 * Default OFF => the existing USER_MESSAGE/task path is byte-identical (no intent
 * field, no deposit branch).
 */
const INTELLIGENT_INGEST_ENABLED =
  process.env.NEXT_PUBLIC_ENABLE_INTELLIGENT_INGEST === "true";

/**
 * v1 HEURISTIC intent classifier (deterministic, no LLM side-call): an empty /
 * whitespace-only message that carries files/attachments is a "deposit" (drop
 * files into the brain, no assistant turn); everything else is a "task".
 */
export function classifyIntent(
  content: string,
  images?: ImageAttachment[],
  files?: FileAttachment[]
): "task" | "deposit" {
  const hasAttachments = Boolean(images?.length || files?.length);
  const isEmptyText = !content || content.trim().length === 0;
  return isEmptyText && hasAttachments ? "deposit" : "task";
}

/** What every fork of one send shares. Minted once, in `sendMessage`. */
type SendContext = {
  seam: TaskStoreSeam;
  socket: ReturnType<typeof getSocket>;
  content: string;
  images: ImageAttachment[] | undefined;
  files: FileAttachment[] | undefined;
  silent: boolean;
  /** Undefined when the intelligent-ingest flag is off. */
  intent: "task" | "deposit" | undefined;
  isDeposit: boolean;
  /** The ONE message identity — thread row id, wire `id`, DB row id. */
  messageId: string;
  /** Client-minted envelope uuid — the staged-card / stash join key. */
  wireUuid: string;
  traceId: string;
  /** The optimistic thread copy of the user's message. */
  userMessage: Message;
  /** A new task's model, named by the caller (SendMessageOptions.model). */
  model: string | undefined;
};

export type SendMessageOptions = {
  silent?: boolean;
  /**
   * A NEW task's model, in place of the composer's pick: the failed ending's
   * "Start again on <default>" (lib/retry-turn.ts). Ignored on a continuation,
   * whose model was fixed when its task was born.
   */
  model?: string;
};

export function sendMessage(
  seam: TaskStoreSeam,
  content: string,
  images?: ImageAttachment[],
  files?: FileAttachment[],
  options?: SendMessageOptions
): void {
  const { get, set } = seam;
  const silent = options?.silent ?? false;
  // Phase 3 intelligent ingest (gated). When OFF, intent is undefined and the
  // payload + optimistic-loader behavior are byte-identical to today.
  const intent = INTELLIGENT_INGEST_ENABLED
    ? classifyIntent(content, images, files)
    : undefined;
  const isDeposit = intent === "deposit";
  ensureConnected();
  const socket = getSocket();
  const { selectedTaskId } = get();
  // ONE message identity (the duplicate-turn disease, staging task d463cf1f):
  // this UUID is the message's id EVERYWHERE — the optimistic thread card,
  // the wire frame's `id`, the server's inbox dedup key (client_message_id),
  // AND the messages-table row id. Because the server persists the row under
  // THIS string, the MESSAGE_SYNC echo can never flip the thread id out from
  // under a retry — so retryConnection / retryMessage naturally resend the
  // exact key the inbox already dedups on. (The old `msg-<ts>-<rand>` mint
  // forced a second, server-minted row id; the sync replace adopted it, and
  // the Retry button then resent the same message under a fresh dedup key —
  // a duplicate persisted row and a re-billed turn.)
  const messageId = crypto.randomUUID();
  // The staging namespace uses THE SAME identity. The envelope `uuid` the server
  // stages under, the staged-card / stash join key, and the `wireUuid` on the
  // optimistic thread message are all this one string — so a staged message and
  // its persisted row can always find each other by id, whichever arrives first
  // and however many syncs replace the local copy in between. A second uuid was
  // the disease behind the double bubbles (staging, 08-30): a MESSAGE_SYNC
  // replaced the thread row with the server's copy, which carried no wireUuid,
  // so the next MESSAGE_STAGED / MESSAGE_PICKED_UP for that message could not
  // see it and painted it again with a fresh timestamp.
  const wireUuid = messageId;
  // Observability: one trace id per send, shared by BOTH branches below.
  // Stamped on the wire payload + the analytics event, and stashed on the
  // store so reportError can correlate a post-send crash to this run.
  const traceId = mintTraceId();
  set((state) => {
    state.lastTraceId = traceId;
  });
  // Expose to non-React diagnostics (reportError) without a circular dep.
  setCurrentTraceId(traceId);

  // P5.4 (Agent Teams) — a leading `@name ` matching a LIVE teammate of the
  // open task routes this send into the TEAMMATE's own thread instead
  // (handlers/teammateSend.ts). An unknown @name is not a route: it returns
  // false and falls through below as plain text to the lead.
  if (selectedTaskId && !silent) {
    const routed = routeTeammateSend({
      get,
      emit: (payload, onResult) => emitUserMessage(socket, payload, onResult),
      selectedTaskId,
      content,
      images,
      files,
      messageId,
      wireUuid,
      traceId,
      isDeposit,
    });
    if (routed) return;
  }

  // Durable HITL: when a task is paused on an interaction and the user sends
  // a fresh message, fire exactly ONE ordered signal — the USER_MESSAGE below.
  // We deliberately do NOT auto-resolve the interaction (no deny/user_redirected
  // HTTP call) and do NOT null `pendingInteraction` here. The server forwards
  // the message to the sandbox's advance-while-paused path, which defers the
  // pause and drives the message as a single serialized turn; the pause stays
  // pending server-side and coexists with the new turn. (The auth card keeps
  // rendering off its committed tool_use block + connection probe, NOT off
  // `pendingInteraction`, so it survives the new turn even though
  // handleAgentStart clears `pendingInteraction` on the next AGENT_START.)
  // The previous double-fire (deny-resolve over HTTP racing the socket
  // message) interrupted the message's own turn; that path is removed.

  // Server owns displayStatus — don't write it from frontend.
  // The server emits CONNECTION_STATE with 'starting' as soon as it begins the wake flow.

  const ctx: SendContext = {
    seam,
    socket,
    content,
    images,
    files,
    silent,
    intent,
    isDeposit,
    messageId,
    wireUuid,
    traceId,
    userMessage: buildUserMessage(messageId, wireUuid, content, images, files),
    model: options?.model,
  };

  if (!selectedTaskId) sendNewTask(ctx);
  else sendContinuation(ctx, selectedTaskId);
}

/** The user's typed text plus any attachments, as the thread renders them. */
function buildContentBlocks(
  content: string,
  images?: ImageAttachment[],
  files?: FileAttachment[]
): ContentBlock[] {
  const contentBlocks: ContentBlock[] = [{ type: "text", text: content }];
  if (images?.length) {
    images.forEach((img) => {
      contentBlocks.push({
        type: "image",
        data: img.data,
        mediaType: img.mediaType,
      });
    });
  }
  if (files?.length) {
    files.forEach((f) => {
      contentBlocks.push({
        type: "file_attachment",
        filename: f.filename,
        url: f.url,
        mediaType: f.mediaType,
        sizeBytes: f.sizeBytes,
      });
    });
  }
  return contentBlocks;
}

function buildUserMessage(
  messageId: string,
  wireUuid: string,
  content: string,
  images?: ImageAttachment[],
  files?: FileAttachment[]
): Message {
  return {
    id: messageId,
    role: "user",
    content: buildContentBlocks(content, images, files),
    timestamp: new Date(),
    agentSlug: DEFAULT_AGENT,
    // The id minted here IS the DB row id (one-identity seam): the server
    // persists the row under this UUID and echoes the same string back
    // (ack.messageId, USER_MESSAGE_BROADCAST, MESSAGE_SYNC rows). It still
    // starts life optimistic and flips to `persisted` at the ack seam — a
    // no-op rename in the common case, but a legacy-window server that
    // minted its own row id returns THAT, and the flip is what keeps every
    // later id-keyed dedup (findMessageByAnyId) matching the echo.
    // `wireUuid` links this thread copy to the staging namespace (see the mint
    // above) so a stray MESSAGE_STAGED for an already-threaded idle send is a
    // no-op instead of a duplicate chip.
    identity: { kind: "optimistic", clientId: messageId, wireUuid },
  };
}

/**
 * Flip the optimistic user message to `persisted` under the id the server
 * returned. Without this the echo keyed by the DB UUID misses the client-minted
 * id and pushes a second copy. A miss is a no-op — a staged (busy) send's
 * message may not be in the thread yet, and the pickup/sync reconcilers cover it.
 */
function adoptServerMessageId(
  seam: TaskStoreSeam,
  taskId: string,
  messageId: string,
  serverId: string
): void {
  seam.get()._mutateTasks((tasks) => {
    const task = tasks.get(taskId);
    if (!task) return;
    const idx = findMessageByAnyId(task.messages, messageId);
    if (idx === -1) return;
    markMessagePersisted(task.messages[idx], serverId);
  });
}

// ===========================================================================
// New task — nothing selected
// ===========================================================================

function sendNewTask(ctx: SendContext): void {
  const {
    seam,
    socket,
    content,
    images,
    files,
    intent,
    isDeposit,
    messageId,
    wireUuid,
    traceId,
  } = ctx;
  const { get, set } = seam;
  // New task — consume the pre-allocated draft UUID (or allocate fresh
  // if the user sent without typing first, e.g. paste-and-send). Using
  // the pre-allocated UUID is how the SDK session warmed during typing
  // matches the eventual real task — without it, send-time pays a fresh
  // ~3.5s SDK init.
  //
  // Guard: don't create two tasks if the user double-sends before the ack returns.
  if (get().pendingSendTaskId) return;

  const taskId = get().getOrAllocatePendingNewTaskId();
  const draftTitle = generateTaskTitle(content);

  // New tasks capture the picked HARNESS and MODEL from the global selectors
  // ONCE, here. From this point the task is locked to both — the server
  // resolves every later turn from the stored values, and the selector
  // renders read-only. The HARNESS is the user's EXPLICIT choice (runtimeStore:
  // Claude→'claude', OpenAI→'openai_responses', Vestra→'native'); the model is
  // picked from that harness's allowed set. An explicit pick rides verbatim; an
  // UNTOUCHED default resolves to the coherent native pair (Luna). See
  // resolveComposerModel; this is the single new-task dispatch seam for both
  // composers.
  // A caller that names the model (Start again on <default>) sends it as an
  // explicit pick on Vestra's own engine, whatever the pill holds.
  const { model: newTaskModel, runtime: newTaskRuntime } = resolveComposerModel(
    ctx.model
      ? { model: ctx.model, picked: true, runtime: "native" }
      : {
          model: useModelStore.getState().model,
          picked: useModelStore.getState().picked,
          runtime: useRuntimeStore.getState().runtime,
          engine: nextTaskEngine(),
        }
  );
  // Draft access-level override picked on the composer before this task
  // existed. Seed the optimistic task so the picker reads it immediately;
  // the SET_ACCESS_LEVEL emit on send-ack persists it server-side (the task
  // exists by then). null ⇒ inherit the user default, no override.
  const newTaskAccessLevel = get().nextTaskAccessLevel;

  seedOptimisticNewTask(ctx, taskId, {
    draftTitle,
    runtime: newTaskRuntime,
    model: newTaskModel,
    accessLevel: newTaskAccessLevel,
  });

  const emitPayload = {
    taskId,
    message: content,
    id: messageId,
    images,
    files,
    targetAgent: DEFAULT_AGENT,
    mode: useModeStore.getState().mode,
    runtime: newTaskRuntime,
    model: newTaskModel,
    // K6 P5: client-minted envelope identity (the FE staged-card join key).
    // Same value stamped on `userMessage.identity.wireUuid` above.
    uuid: wireUuid,
    origin: { kind: "human" as const },
    surface: isDesktopShell() ? "native" : "web",
    // Observability: trace this send end-to-end (server reads data.traceId).
    traceId,
    // Phase 3 intelligent ingest (gated): undefined when the flag is off.
    ...(intent && { intent }),
  };
  // Analytics v1: one delegation:submitted per user send, new task or not (it
  // replaced task:created + task:message_sent, which a new task sent both of).
  captureDelegationSubmitted(taskId, content, traceId, ctx);
  emitUserMessage(socket, emitPayload, (ack) => {
    if (!ack) return failNewTaskOnTransport(seam, taskId, messageId);
    // The server could not RECORD the message (its inbox append failed). It
    // created the task and marked it failed; keep it here, words and all, so
    // Retry resends under the same task id and the server revives that task
    // instead of each attempt leaving another failed task behind.
    const unrecorded = notRecordedCode(ack);
    if (!ack.success && unrecorded) {
      // The task row exists server-side, so the access level picked before
      // sending is persisted now, exactly as the success path does.
      persistNewTaskAccessLevel(socket, taskId, newTaskAccessLevel);
      return failNewTaskOnTransport(seam, taskId, messageId, unrecorded);
    }
    if (!ack.success) {
      // Server VERDICT rejection (credit gate, rate limit, create failure):
      // delivery is certain and the answer is no — roll back the optimistic
      // task + user message, exactly as before.
      set((state) => {
        state.pendingSendTaskId = null;
        state.tasks.delete(taskId);
        if (state.selectedTaskId === taskId) state.selectedTaskId = null;
      });
      handBackRefusedWords(ctx, ack, null);
      console.warn("[sendMessage] Failed:", ack.error);
      return;
    }
    settleNewTaskAck(ctx, taskId, ack, newTaskAccessLevel);
  });
}

/**
 * Consume the draft UUID + insert the optimistic task in a SINGLE state
 * mutation. Splitting these into two `set` calls created an intermediate frame
 * where pendingNewTaskId was null but the task didn't exist yet — a subscriber
 * re-rendering in that window could allocate a fresh UUID and fire a spurious
 * SANDBOX_PRE_WARM.
 */
function seedOptimisticNewTask(
  ctx: SendContext,
  taskId: string,
  seed: {
    draftTitle: string;
    runtime: ReturnType<typeof resolveComposerModel>["runtime"];
    model: string | undefined;
    accessLevel: ReturnType<TaskStoreSeam["get"]>["nextTaskAccessLevel"];
  }
): void {
  const {
    seam: { set },
    isDeposit,
    traceId,
    userMessage,
  } = ctx;
  // Optimistic footer loader: show the ThinkingIndicator the instant the user
  // sends, instead of waiting ~1-2s for the server's AGENT_START round-trip
  // (client→server→sandbox→agent). The loader renders on an in-flight assistant
  // message gated by isProcessing, so we set phase='processing' AND seed an empty
  // assistant streamingMessage here — mirroring handleAgentStart's placeholder.
  // The real AGENT_START is then idempotent-skipped (its guard is
  // phase==='processing' && streamingMessage), and the first TEXT_DELTA appends
  // to this message. On ack failure the whole optimistic task (loader included)
  // is deleted in the ack callback below. Existing-task sends already feel
  // instant (they set phase='waiting' and the loader shows on the prior bubble);
  // this brings new tasks to parity.
  const optimisticAssistantId = `msg-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;
  set((state) => {
    state.pendingNewTaskId = null;
    // Draft consumed into this task; clear it so the next new-task draft
    // starts from the user default again.
    state.nextTaskAccessLevel = null;
    state.pendingSendTaskId = taskId;
    // This client minted the id: a sync that outruns the server's row insert
    // reads as "not yet", never as someone else's task (syncHandlers).
    const mintedHereAt = Date.now();
    // Deposit: there is NO assistant turn, so do NOT seed the optimistic
    // streaming loader — it would hang the footer in "processing" forever.
    // The task stays idle; the DEPOSIT_ACK records a "saved to your brain" chip.
    state.tasks.set(
      taskId,
      buildNewTask(
        taskId,
        DEFAULT_AGENT,
        isDeposit
          ? {
              messages: [userMessage],
              phase: "idle",
              title: seed.draftTitle,
              runtime: seed.runtime,
              model: seed.model,
              accessLevel: seed.accessLevel,
              mintedHereAt,
            }
          : {
              messages: [userMessage],
              mintedHereAt,
              phase: "processing",
              streamingMessage: {
                id: optimisticAssistantId,
                role: "assistant",
                content: [],
                timestamp: new Date(),
                agentSlug: DEFAULT_AGENT,
                identity: {
                  kind: "optimistic",
                  clientId: optimisticAssistantId,
                },
              },
              turnStartTime: Date.now(),
              title: seed.draftTitle,
              runtime: seed.runtime,
              model: seed.model,
              accessLevel: seed.accessLevel,
            }
      )
    );
    // Perceived-TTFT clock — armed on the new task's optimistic turn. A
    // deposit has no assistant turn, so it never arms (no first token comes).
    if (!isDeposit) {
      const seeded = state.tasks.get(taskId);
      if (seeded) armPerceivedTtft(seeded, "new_task", traceId);
    }
    state.selectedTaskId = taskId;
  });
}

/**
 * TRANSPORT failure (no socket / ack timed out): delivery is UNKNOWN, so
 * deleting the optimistic task would eat the user's typed message. Keep it,
 * stop the optimistic loader, and mark the message "Not sent" so the Retry
 * affordance takes over. If the send actually landed, the reconnect sync
 * reconciles and the server dedups any retry by this client id.
 *
 * Also the NOT-RECORDED refusal (`code` set): the server created the task
 * and could not record the message, so the task exists there too, marked
 * failed. Same keep: the Retry under this task id revives it server-side.
 * That task is real, so pendingSendTaskId stays set, as on success (see
 * settleNewTaskAck): it is TaskView's only bridge to the task on the /task
 * composer flow, whose URL never catches up (history.replaceState), and
 * clearing it drops the view to the greeting with the words out of sight.
 */
function failNewTaskOnTransport(
  seam: TaskStoreSeam,
  taskId: string,
  messageId: string,
  code?: NotRecordedCode
): void {
  seam.set((state) => {
    if (!code) state.pendingSendTaskId = null;
    const t = state.tasks.get(taskId);
    if (!t) return;
    t.phase = "idle";
    t.streamingMessage = null;
    const idx = findMessageByAnyId(t.messages, messageId);
    if (idx !== -1) {
      t.messages[idx].sendFailed = true;
      t.messages[idx].sendFailedCode = code;
    }
  });
  console.warn(
    code
      ? `[sendMessage] new-task message not recorded (${code}), kept and marked Not sent`
      : "[sendMessage] new-task send timed out or socket unavailable, marked Not sent"
  );
}

/**
 * Persist the pre-send access override once the task exists server-side (the
 * server's SET_ACCESS_LEVEL handler requires the DB row). Emitting before the
 * ack would race task creation and drop the override. null means nothing to
 * persist (inherit the user default).
 */
function persistNewTaskAccessLevel(
  socket: SendContext["socket"],
  taskId: string,
  level: ReturnType<TaskStoreSeam["get"]>["nextTaskAccessLevel"]
): void {
  if (!level) return;
  socket?.emit(EVENTS.SET_ACCESS_LEVEL, { taskId, level });
}

function settleNewTaskAck(
  ctx: SendContext,
  taskId: string,
  ack: SendAck,
  newTaskAccessLevel: ReturnType<TaskStoreSeam["get"]>["nextTaskAccessLevel"]
): void {
  const { seam, socket, messageId } = ctx;
  const { get, set } = seam;
  // Identity reconciliation — the server assigned a DB row id (UUID) and
  // returned it as ack.messageId. (The broadcast/sync handlers reconcile by
  // content too, so an echo that races ahead of this ack is still safe.)
  if (ack.messageId)
    adoptServerMessageId(seam, taskId, messageId, ack.messageId);

  persistNewTaskAccessLevel(socket, taskId, newTaskAccessLevel);

  // Success — keep pendingSendTaskId alive. It's the bridge between
  // the optimistic store state and TaskView's `urlTaskId`, which
  // will NOT update after a first-message send because the layout
  // uses `window.history.replaceState` (bypasses Next.js router, so
  // `useParams()` never learns the URL changed). Clearing here races
  // the UI: ack lands in ~100ms, urlTaskId never catches up →
  // `activeTaskId = urlTaskId ?? pendingSendTaskId ?? null`
  // collapses to null and the body reverts to `<GreetingView />`
  // while the agent is mid-stream. pendingSendTaskId is cleared in
  // createNewTask / switchTask instead, when we actually navigate
  // away.

  // Success — nothing to migrate. The taskId we sent is the one the server used.
  // (Phase 1 safety check: if the server somehow returned a different id via
  // the legacy path, fall through gracefully. This branch should not fire for
  // new clients and will be removed in Phase 3.)
  const confirmedId = ack.taskId ?? taskId;
  if (confirmedId !== taskId) {
    console.warn(
      `[sendMessage] Server returned a different taskId (${confirmedId}) than we sent (${taskId}). Treating as legacy path.`
    );
    set((state) => {
      const ourTask = state.tasks.get(taskId);
      state.tasks.delete(taskId);
      if (ourTask && !state.tasks.has(confirmedId)) {
        state.tasks.set(confirmedId, { ...ourTask, taskId: confirmedId });
      }
      if (state.selectedTaskId === taskId) state.selectedTaskId = confirmedId;
    });
  }
  setStoredTaskId(confirmedId);
  schedulePersist(get().tasks);
}

// ===========================================================================
// Continuation — an open task
// ===========================================================================

function sendContinuation(ctx: SendContext, selectedTaskId: string): void {
  const {
    seam,
    socket,
    content,
    images,
    files,
    silent,
    intent,
    messageId,
    wireUuid,
    traceId,
  } = ctx;
  const { get } = seam;
  // A send is never dropped. The old rapid-double-send guard returned here while
  // the task was 'waiting' (sent, turn not yet open) — and the composer clears
  // its draft the moment it hands the text over, so a message typed while the
  // computer was still waking simply vanished (founder, 08-30: "an important
  // message disappeared"). The durable inbox orders and dedups sends; a second
  // message while the first is still queued is just the next queued message.
  const existingTask = get().tasks.get(selectedTaskId);

  // Browser-fold: a follow-up on a task whose browser pane is SUSPENDED
  // (frozen frame, nobody watching) folds it into the parked chip and
  // releases pane demand BEFORE the send's wake — otherwise the server's
  // hibernate-resume re-mint revives the surface on every turn whether or
  // not the turn browses. No-op for live panes, the desktop shell, and
  // the machine-flag-OFF legacy path (all guarded inside).
  foldSuspendedBrowserOnSend(socket, selectedTaskId);

  // K6 P5 (staged-only-until-pickup): when the agent is busy, the message is
  // represented by a staged tray card (created server-side via MESSAGE_STAGED),
  // NOT by an immediate thread entry. Pushing it into `task.messages` here too
  // is the double-render bug. Suppress the thread-push while busy and stash the
  // object (below) so MESSAGE_PICKED_UP can promote the exact message into the
  // thread once the agent pulls it in. Idle sends are unchanged (instant entry).
  // 'waiting' (sent, turn not yet open) is busy too: the message queues behind
  // the one still being dispatched and lands in the thread when it is picked up.
  const midTurn =
    existingTask?.phase === "processing" ||
    existingTask?.phase === "interrupting";
  const isBusy = midTurn || existingTask?.phase === "waiting";

  applyContinuationOptimism(ctx, selectedTaskId, isBusy);
  if (isBusy && !silent) stageOptimisticCard(ctx, selectedTaskId, midTurn);

  // Continuation: send the TASK's locked engine, not the global selector
  // (the server ignores this on continuations anyway, but keep the client
  // honest). Falls back to the selector only if the task somehow lacks one.
  // P1.1: a typed mid-turn send (agent busy) steers the SAME turn — tag it
  // priority:'now' so the sandbox injects it at the next tool-round boundary.
  // A send behind a turn that has not opened yet, and idle continuations, omit
  // priority ⇒ byte-identical payload.
  const emitPayload = {
    taskId: selectedTaskId,
    sessionId: selectedTaskId,
    message: content,
    id: messageId,
    images,
    files,
    targetAgent: DEFAULT_AGENT,
    mode: useModeStore.getState().mode,
    runtime: existingTask?.runtime ?? useRuntimeStore.getState().runtime,
    model: existingTask?.model,
    uuid: wireUuid,
    origin: { kind: "human" as const },
    surface: isDesktopShell() ? "native" : "web",
    traceId,
    ...(midTurn && { priority: "now" as const }),
    ...(silent && { silent: true }),
    ...(intent && { intent }),
  };
  captureDelegationSubmitted(selectedTaskId, content, traceId, ctx);

  // Busy sends own a staged card + stash optimistically — roll BOTH back if the
  // server rejects the send (don't idle the task: its current turn is still
  // running). Idle sends use the standard onFail (thread entry → sendFailed).
  const stagedSend = isBusy && !silent;
  const onSendFailure = stagedSend
    ? (ack?: SendAck) => rollbackStagedCard(ctx, selectedTaskId, ack)
    : (ack?: SendAck) => failThreadedSend(ctx, selectedTaskId, ack);
  // Identity reconciliation, continuation edition — the same seam the
  // new-task branch runs above. With the one-identity contract the ack
  // echoes the id we sent (the flip is a no-op rename), but a
  // legacy-window server that minted its OWN row id returns that instead
  // — consuming it is what keeps every later id-keyed lookup honest. A
  // staged (busy) send's message may not be in the thread yet; the miss
  // is a no-op and the pickup/sync reconcilers cover it.
  emitUserMessage(socket, emitPayload, (ack) => {
    onSendFailure(ack);
    if (!ack?.success || !ack.messageId) return;
    adoptServerMessageId(seam, selectedTaskId, messageId, ack.messageId);
  });
}

function applyContinuationOptimism(
  ctx: SendContext,
  selectedTaskId: string,
  isBusy: boolean
): void {
  const {
    seam: { get },
    content,
    images,
    files,
    silent,
    isDeposit,
    traceId,
    userMessage,
  } = ctx;
  // `/compact` is a control command that forces a manual compaction (native +
  // Claude arms). Optimistically light up the compaction loader the instant the
  // user sends: the polished "Optimizing context…" indicator already exists
  // (ThinkingIndicator's `sdkStatus === 'compacting'` branch) but is driven by
  // the Claude-arm-only AGENT_STATUS event — the native arm never emits it, so
  // native `/compact` otherwise shows the generic "Thinking…". Setting sdkStatus
  // here makes the nice loader arm-agnostic AND instant. Cleared on AGENT_END
  // (turn-end resets sdkStatus → null). Gated exactly like the sandbox's
  // interception: a bare `/compact` with no attachments (an attached file
  // rewrites it to a normal turn server-side, so we must NOT show the loader).
  const isCompactCommand =
    content.trim() === "/compact" && !images?.length && !files?.length;

  get()._mutateTasks((tasks) => {
    const task = tasks.get(selectedTaskId);
    if (!task) return;
    if (!silent && !isBusy) task.messages.push(userMessage);
    task.lastActivityAt = new Date();
    // Deposit: no assistant turn follows, so do NOT flip to 'waiting' (that
    // surfaces a loader on the prior bubble that would never resolve).
    if (!isDeposit && task.phase !== "processing") {
      task.phase = "waiting";
    }
    if (isCompactCommand) task.sdkStatus = "compacting";
    // Perceived-TTFT clock — only for a fresh idle continuation that STARTS a
    // turn. A busy/priority send steers a turn already mid-stream (its next
    // delta isn't a first token), a silent send isn't user-perceived, and a
    // deposit has no assistant turn — none of those arm.
    if (!isBusy && !silent && !isDeposit) {
      armPerceivedTtft(task, "continuation", traceId);
    }
  });
}

/**
 * Optimistic staged tray card — the SINGLE record for this in-flight message.
 * It renders INSTANTLY (no FE→server→FE hop for MESSAGE_STAGED) and carries the
 * suppressed `Message` on `card.message` so MESSAGE_PICKED_UP promotes the EXACT
 * object. The message starts optimistic (clientId = the client-minted UUID); a later
 * MESSAGE_SYNC / USER_MESSAGE_BROADCAST reconciles it to the DB UUID by content
 * (see findOptimisticUserMessage), and pickup's findMessageByAnyId dedup keys off
 * the same clientId. Keyed by the SAME wireUuid the server stages under, so
 * handleStaged (idempotent by uuid) no-ops on the echo — no double card. Rolled
 * back below if the send is rejected; because the message rides on the card, every
 * teardown (cancel / STAGING_SYNC / handleAgentEnd) drops it in one move — no
 * shadow record can orphan. Only when busy + not silent — an idle send is already
 * in the thread.
 */
function stageOptimisticCard(
  ctx: SendContext,
  selectedTaskId: string,
  midTurn: boolean
): void {
  const {
    seam: { set },
    content,
    wireUuid,
    userMessage,
  } = ctx;
  set((state) => {
    const list = state.stagedMessages.get(selectedTaskId) || [];
    if (!list.some((m) => m.uuid === wireUuid)) {
      state.stagedMessages.set(selectedTaskId, [
        ...list,
        // P1.3: mirror the same priority:'now' the emitPayload carries for a
        // mid-turn send, so the tray hint renders instantly (no FE→server→FE round trip).
        {
          uuid: wireUuid,
          content,
          taskId: selectedTaskId,
          origin: { kind: "human" as const },
          surface: isDesktopShell() ? "native" : "web",
          ...(midTurn ? { priority: "now" as const } : {}),
          stagedAt: Date.now(),
          message: userMessage,
        },
      ]);
    }
  });
}

/**
 * Treat a MISSING ack (socket timeout / drop) as a failure too — leaving
 * the optimistic card up on a dropped send would orphan it until a later
 * STAGING_SYNC/AGENT_END clears it. (emitUserMessage's ack timeout is
 * what makes this branch actually reachable.)
 */
function rollbackStagedCard(
  ctx: SendContext,
  selectedTaskId: string,
  ack?: SendAck
): void {
  if (ack?.success) return;
  const {
    seam: { set },
    wireUuid,
  } = ctx;
  set((state) => {
    const list = (state.stagedMessages.get(selectedTaskId) || []).filter(
      (m) => m.uuid !== wireUuid
    );
    if (list.length === 0) state.stagedMessages.delete(selectedTaskId);
    else state.stagedMessages.set(selectedTaskId, list);
  });
  handBackRefusedWords(ctx, ack, selectedTaskId);
  console.warn(
    "[sendMessage] staged send rejected/timed out — rolled back optimistic card:",
    ack?.error
  );
}

/**
 * A refused send is never silent. Prod 09-03: a follow-up typed to a BUSY task
 * was refused for credits, its staged card rolled back, and the words went
 * with it — "was lost somehow" (founder). The words go back to the composer
 * that owns them (the task's own box, appended so a newer draft survives; the
 * home box when no task took them) and the home history says why. The reason
 * is the server's structured code, never its error text; a bare refusal or a
 * dropped transport reads "Not sent" alone.
 */
function handBackRefusedWords(
  ctx: SendContext,
  ack: SendAck | undefined,
  taskId: string | null
): void {
  const { content, silent } = ctx;
  if (silent || !content.trim()) return;
  const draft = useComposerDraftStore.getState();
  if (taskId) draft.appendPrefill(taskId, content);
  else draft.prefillHome(content);
  useComposerHistoryStore.getState().add({
    question: content,
    kind: "error",
    text: ack?.code === "credits_exhausted" ? "Out of credits" : "",
    ...(taskId ? { taskId } : {}),
  });
}

/**
 * Marks the message as failed in whichever task owns it. A MISSING ack
 * (transport failure via emitUserMessage) takes the same visible path: the
 * message shows "Not sent · Retry" instead of sitting there looking sent.
 */
function failThreadedSend(
  ctx: SendContext,
  targetTaskId: string,
  ack?: SendAck
): void {
  if (ack?.success) return;
  const {
    seam: { get },
    messageId,
  } = ctx;
  get()._mutateTasks((tasks) => {
    const task = tasks.get(targetTaskId);
    if (!task) return;
    task.phase = "idle";
    const idx = findMessageByAnyId(task.messages, messageId);
    if (idx !== -1) {
      task.messages[idx].sendFailed = true;
      task.messages[idx].sendFailedCode = notRecordedCode(ack);
    }
  });
  console.warn("[sendMessage] Failed:", ack?.error ?? "transport failure");
}
